// Item 61 — Quick Calculations / "Show Value As"
// Client-side post-processing of a governed/adhoc query result. Applies per-measure
// transforms (running total, % of total, rank, moving average, YoY/MoM/QoQ/WoW, etc.)
// without hitting the backend. Consumed by WidgetRenderer for charts/tables/KPIs.
//
// A quick-calc lives on a measure config as:
//   { key/field/agg..., quickCalc: { kind, window?, period? } }
//
// Kinds:
//   "none"           - passthrough
//   "pct_total"      - value / grand-total × 100 (%)
//   "running"        - cumulative sum along the primary dimension order
//   "rank"           - dense rank (1..N), configurable direction
//   "moving_avg"     - trailing N-point average (window from qc.window || 3)
//   "diff_prior"     - value - previous value
//   "pct_diff_prior" - (value - prev) / prev × 100 (%)
//   "yoy" | "mom" | "qoq" | "wow"
//                    - period-over-period % change, offset derived from the date_part
//                      of the primary dimension.
//
// The transforms operate on a copy of data.rows and update measure.format metadata
// (adds `is_pct: true` when the value becomes a percentage) so downstream
// formatValue()/formatNumber() renders the right units.

export const QUICK_CALCS = [
  { value: "none", label: "None (raw value)" },
  { value: "pct_total", label: "% of grand total" },
  { value: "running", label: "Running total" },
  { value: "rank", label: "Rank (highest = 1)" },
  { value: "moving_avg", label: "Moving average" },
  { value: "diff_prior", label: "Difference from previous" },
  { value: "pct_diff_prior", label: "% difference from previous" },
  { value: "yoy", label: "Year over year %" },
  { value: "mom", label: "Month over month %" },
  { value: "qoq", label: "Quarter over quarter %" },
  { value: "wow", label: "Week over week %" },
];

export const CALC_IS_PCT = new Set(["pct_total", "pct_diff_prior", "yoy", "mom", "qoq", "wow"]);

function measureKey(m) {
  if (!m) return null;
  if (m.key) return m.key;
  if (m.alias) return m.alias;
  if (m.field) return `${m.agg || "sum"}_${m.field}`;
  return null;
}

function dimKey(d) {
  if (!d) return null;
  if (d.key) return d.key;
  if (d.alias) return d.alias;
  if (d.date_part && d.date_part !== "none") return `${d.field}_${d.date_part}`;
  return d.field;
}

// Offset (in rows) for period-over-period calcs given the primary dimension's date_part.
// Rows are assumed to be ordered chronologically at the widget's date grain.
function periodOffset(kind, datePart) {
  const raw = (datePart || "").toLowerCase();
  // Item 35: hierarchy aliases map onto their grain
  const dp = { week_year: "week", week_number: "week", date_hour: "day" }[raw] || raw;
  const map = {
    yoy: { year: 1, quarter: 4, month: 12, week: 52, day: 365 },
    qoq: { quarter: 1, month: 3, week: 13, day: 90 },
    mom: { month: 1, week: 4, day: 30 },
    wow: { week: 1, day: 7 },
  };
  return (map[kind] && (map[kind][dp] || 1)) || 1;
}

// Rank helper: dense rank on numeric values, direction "desc" or "asc"
function denseRank(values, direction = "desc") {
  const idx = values.map((v, i) => ({ v: Number.isFinite(Number(v)) ? Number(v) : null, i }));
  idx.sort((a, b) => {
    if (a.v == null && b.v == null) return 0;
    if (a.v == null) return 1;
    if (b.v == null) return -1;
    return direction === "asc" ? a.v - b.v : b.v - a.v;
  });
  const ranks = new Array(values.length).fill(null);
  let last = null, r = 0, effR = 0;
  idx.forEach((x, k) => {
    if (x.v == null) { ranks[x.i] = null; return; }
    effR = k + 1;
    if (x.v !== last) { r = effR; last = x.v; }
    ranks[x.i] = r;
  });
  return ranks;
}

function transformOne(rows, mKey, qc, dim0DatePart) {
  const kind = qc.kind || "none";
  if (kind === "none" || !mKey) return rows;
  const vals = rows.map((r) => Number(r[mKey]));

  if (kind === "pct_total") {
    const tot = vals.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
    return rows.map((r, i) => ({ ...r, [mKey]: tot ? (vals[i] / tot) * 100 : null }));
  }
  if (kind === "running") {
    let acc = 0;
    return rows.map((r, i) => { if (Number.isFinite(vals[i])) acc += vals[i]; return { ...r, [mKey]: acc }; });
  }
  if (kind === "rank") {
    const ranks = denseRank(vals, qc.direction || "desc");
    return rows.map((r, i) => ({ ...r, [mKey]: ranks[i] }));
  }
  if (kind === "moving_avg") {
    const w = Math.max(1, Math.min(50, Number(qc.window) || 3));
    const out = new Array(vals.length).fill(null);
    let acc = 0, count = 0;
    const queue = [];
    for (let i = 0; i < vals.length; i++) {
      const v = Number.isFinite(vals[i]) ? vals[i] : null;
      queue.push(v);
      if (v != null) { acc += v; count += 1; }
      if (queue.length > w) {
        const old = queue.shift();
        if (old != null) { acc -= old; count -= 1; }
      }
      out[i] = count > 0 ? acc / count : null;
    }
    return rows.map((r, i) => ({ ...r, [mKey]: out[i] }));
  }
  if (kind === "diff_prior") {
    return rows.map((r, i) => {
      if (i === 0 || !Number.isFinite(vals[i]) || !Number.isFinite(vals[i - 1])) return { ...r, [mKey]: null };
      return { ...r, [mKey]: vals[i] - vals[i - 1] };
    });
  }
  if (kind === "pct_diff_prior") {
    return rows.map((r, i) => {
      if (i === 0 || !Number.isFinite(vals[i]) || !Number.isFinite(vals[i - 1]) || vals[i - 1] === 0) return { ...r, [mKey]: null };
      return { ...r, [mKey]: ((vals[i] - vals[i - 1]) / vals[i - 1]) * 100 };
    });
  }
  if (["yoy", "mom", "qoq", "wow"].includes(kind)) {
    const off = periodOffset(kind, dim0DatePart);
    return rows.map((r, i) => {
      if (i < off || !Number.isFinite(vals[i]) || !Number.isFinite(vals[i - off]) || vals[i - off] === 0) return { ...r, [mKey]: null };
      return { ...r, [mKey]: ((vals[i] - vals[i - off]) / vals[i - off]) * 100 };
    });
  }
  return rows;
}

// Public API: applies every measure's `quickCalc` transform (if any) to the query result.
// Also decorates the meta so formatValue() renders % where appropriate.
export function applyQuickCalcs(data, config) {
  if (!data || !data.rows || !data.rows.length) return data;
  const measures = (config.measures || []).filter((m) => m && m.quickCalc && m.quickCalc.kind && m.quickCalc.kind !== "none");
  if (!measures.length) return data;
  const dim0 = (config.dimensions || [])[0];
  const dim0DatePart = dim0 && dim0.date_part && dim0.date_part !== "none" ? dim0.date_part : null;

  let rows = data.rows.map((r) => ({ ...r }));
  const metaMeas = [...(((data.meta || {}).measures) || [])];
  const measByKey = Object.fromEntries(metaMeas.map((m, i) => [m.key, { i, m }]));

  measures.forEach((m) => {
    const key = measureKey(m);
    if (!key) return;
    rows = transformOne(rows, key, m.quickCalc, dim0DatePart);
    if (CALC_IS_PCT.has(m.quickCalc.kind)) {
      // Values are already multiplied by 100; render with a suffix "%" (avoid formatValue's ×100 in "percent" type).
      const pctFormat = { type: "number", decimals: 1, suffix: "%" };
      if (measByKey[key]) {
        metaMeas[measByKey[key].i] = { ...measByKey[key].m, format: pctFormat };
      } else {
        metaMeas.push({ key, format: pctFormat });
      }
    } else if (m.quickCalc.kind === "rank") {
      const rankFormat = { type: "number", decimals: 0 };
      if (measByKey[key]) metaMeas[measByKey[key].i] = { ...measByKey[key].m, format: rankFormat };
      else metaMeas.push({ key, format: rankFormat });
    }
  });

  return { ...data, rows, meta: { ...(data.meta || {}), measures: metaMeas } };
}

// Utility used by exports: build a short "applied filters" banner string.
export function describeFilters(filters = [], globalFilters = []) {
  const all = [...(filters || []), ...(globalFilters || [])].filter((f) => f && (f.field || f.key));
  if (!all.length) return "";
  const bits = all.map((f) => {
    const v = Array.isArray(f.value) ? f.value.join(", ") : String(f.value ?? "");
    const op = f.op || "eq";
    return `${f.field || f.key} ${op} ${v}`;
  });
  return bits.join(" · ");
}

// Consumer helper: derive the current dimension key used for ordering / lookback.
export function primaryDimKey(config) {
  return dimKey((config.dimensions || [])[0]);
}
